import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Badge
} from '@mui/material';
import {
  ExpandMore,
  Schedule,
  PlayArrow,
  CheckCircle,
  Error as ErrorIcon,
  Cancel,
  Refresh,
  Info
} from '@mui/icons-material';
import { Config } from '../config/config';
import { ScheduledRecordingService, ScheduledClip, ClipStats } from '../service/scheduledRecording.service';

interface GroupedClips {
  [key: string]: ScheduledClip[];
}

const ComprehensiveClipView: React.FC = () => {
  const [clipStats, setClipStats] = useState<ClipStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<string>('all');
  const [cameraFilter, setCameraFilter] = useState<string>('all');
  const [activeTab, setActiveTab] = useState(0);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadClipData();
  }, []);

  const loadClipData = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await ScheduledRecordingService.getAllClips();
      setClipStats(data);
    } catch (err: any) {
      console.error('Failed to load clip data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load clip data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled': return 'warning';
      case 'recording': return 'info';
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'scheduled': return <Schedule />;
      case 'recording': return <PlayArrow />;
      case 'completed': return <CheckCircle />;
      case 'failed': return <ErrorIcon />;
      case 'cancelled': return <Cancel />;
      default: return <Info />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('de-DE');
  };

  const getUniqueValues = (key: keyof ScheduledClip) => {
    if (!clipStats?.clips_sample) return [];
    const uniqueSet = new Set(clipStats.clips_sample.map(clip => clip[key]));
    return Array.from(uniqueSet).sort();
  };

  const getFilteredClips = () => {
    if (!clipStats?.clips_sample) return [];
    
    return clipStats.clips_sample.filter(clip => {
      const statusMatch = filter === 'all' || clip.status === filter;
      const dateMatch = dateFilter === 'all' || formatDate(clip.scheduled_date) === dateFilter;
      const cameraMatch = cameraFilter === 'all' || clip.camera_id === cameraFilter;
      
      return statusMatch && dateMatch && cameraMatch;
    });
  };

  const groupClipsByCamera = () => {
    const filtered = getFilteredClips();
    const grouped: GroupedClips = {};
    
    filtered.forEach(clip => {
      const key = `${clip.camera_name} (${clip.camera_id})`;
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(clip);
    });

    // Sort clips within each group by clip_index
    Object.keys(grouped).forEach(key => {
      grouped[key].sort((a, b) => a.clip_index - b.clip_index);
    });

    return grouped;
  };

  const groupClipsByDate = () => {
    const filtered = getFilteredClips();
    const grouped: GroupedClips = {};
    
    filtered.forEach(clip => {
      const dateKey = formatDate(clip.scheduled_date);
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(clip);
    });

    // Sort clips within each group by camera and clip_index
    Object.keys(grouped).forEach(key => {
      grouped[key].sort((a, b) => {
        if (a.camera_name !== b.camera_name) {
          return a.camera_name.localeCompare(b.camera_name);
        }
        return a.clip_index - b.clip_index;
      });
    });

    return grouped;
  };

  const toggleGroup = (groupKey: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupKey)) {
      newExpanded.delete(groupKey);
    } else {
      newExpanded.add(groupKey);
    }
    setExpandedGroups(newExpanded);
  };

  const getStatusSummary = (clips: ScheduledClip[]) => {
    const summary: { [key: string]: number } = {};
    clips.forEach(clip => {
      summary[clip.status] = (summary[clip.status] || 0) + 1;
    });
    return summary;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
        <Button onClick={loadClipData} sx={{ ml: 2 }}>
          Retry
        </Button>
      </Alert>
    );
  }

  if (!clipStats) {
    return (
      <Alert severity="info">
        Keine Clip-Daten verfügbar
      </Alert>
    );
  }

  const filteredClips = getFilteredClips();
  const groupedByCamera = groupClipsByCamera();
  const groupedByDate = groupClipsByDate();

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h2">
            Alle Clips ({clipStats.total_clips} total)
          </Typography>
          <Button
            startIcon={<Refresh />}
            onClick={loadClipData}
            variant="outlined"
          >
            Aktualisieren
          </Button>
        </Box>

        {/* Status Overview */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {clipStats.status_breakdown.map((status) => (
            <Grid item key={status._id} xs={6} md={3}>
              <Card variant="outlined">
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getStatusIcon(status._id)}
                    <Typography variant="h6">
                      {status.count}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {status._id}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Filters */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filter}
                label="Status"
                onChange={(e) => setFilter(e.target.value)}
              >
                <MenuItem value="all">Alle Status</MenuItem>
                {clipStats.status_breakdown.map((status) => (
                  <MenuItem key={status._id} value={status._id}>
                    {status._id} ({status.count})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Datum</InputLabel>
              <Select
                value={dateFilter}
                label="Datum"
                onChange={(e) => setDateFilter(e.target.value)}
              >
                <MenuItem value="all">Alle Datum</MenuItem>
                {clipStats.date_breakdown.map((date) => (
                  <MenuItem key={date._id} value={date._id}>
                    {date._id} ({date.count})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Kamera</InputLabel>
              <Select
                value={cameraFilter}
                label="Kamera"
                onChange={(e) => setCameraFilter(e.target.value)}
              >
                <MenuItem value="all">Alle Kameras</MenuItem>
                {getUniqueValues('camera_name').map((camera) => (
                  <MenuItem key={camera} value={getUniqueValues('camera_id').find(id => 
                    clipStats.clips_sample.find(c => c.camera_name === camera)?.camera_id === id
                  )}>
                    {camera}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {filteredClips.length} von {clipStats.total_clips} Clips
            </Typography>
          </Grid>
        </Grid>

        {/* Tabs for different views */}
        <Tabs value={activeTab} onChange={(e, value) => setActiveTab(value)} sx={{ mb: 2 }}>
          <Tab label={
            <Badge badgeContent={Object.keys(groupedByCamera).length} color="primary">
              Nach Kamera
            </Badge>
          } />
          <Tab label={
            <Badge badgeContent={Object.keys(groupedByDate).length} color="primary">
              Nach Datum
            </Badge>
          } />
          <Tab label="Tabelle" />
        </Tabs>

        {/* Camera View */}
        {activeTab === 0 && (
          <Box>
            {Object.entries(groupedByCamera).map(([cameraKey, clips]) => {
              const isExpanded = expandedGroups.has(cameraKey);
              const statusSummary = getStatusSummary(clips);
              
              return (
                <Accordion 
                  key={cameraKey} 
                  expanded={isExpanded}
                  onChange={() => toggleGroup(cameraKey)}
                  sx={{ mb: 1 }}
                >
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                      <Typography variant="h6">
                        {cameraKey}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mr: 2 }}>
                        <Chip label={`${clips.length} Clips`} size="small" />
                        {Object.entries(statusSummary).map(([status, count]) => (
                          <Chip
                            key={status}
                            label={`${status}: ${count}`}
                            color={getStatusColor(status) as any}
                            size="small"
                          />
                        ))}
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Clip Index</TableCell>
                            <TableCell>Dateiname</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Datum</TableCell>
                            <TableCell>Pfad</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {clips.map((clip) => (
                            <TableRow key={clip._id}>
                              <TableCell>{clip.clip_index}</TableCell>
                              <TableCell>{clip.filename}</TableCell>
                              <TableCell>
                                <Chip
                                  icon={getStatusIcon(clip.status)}
                                  label={clip.status}
                                  color={getStatusColor(clip.status) as any}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>{formatDate(clip.scheduled_date)}</TableCell>
                              <TableCell>
                                <Typography variant="caption" color="text.secondary">
                                  {clip.file_path}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        )}

        {/* Date View */}
        {activeTab === 1 && (
          <Box>
            {Object.entries(groupedByDate).map(([dateKey, clips]) => {
              const isExpanded = expandedGroups.has(dateKey);
              const statusSummary = getStatusSummary(clips);
              const cameraCounts = clips.reduce((acc, clip) => {
                acc[clip.camera_name] = (acc[clip.camera_name] || 0) + 1;
                return acc;
              }, {} as { [key: string]: number });
              
              return (
                <Accordion 
                  key={dateKey} 
                  expanded={isExpanded}
                  onChange={() => toggleGroup(dateKey)}
                  sx={{ mb: 1 }}
                >
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                      <Typography variant="h6">
                        {dateKey}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mr: 2 }}>
                        <Chip label={`${clips.length} Clips`} size="small" />
                        <Chip label={`${Object.keys(cameraCounts).length} Kameras`} size="small" color="info" />
                        {Object.entries(statusSummary).map(([status, count]) => (
                          <Chip
                            key={status}
                            label={`${status}: ${count}`}
                            color={getStatusColor(status) as any}
                            size="small"
                          />
                        ))}
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Kamera</TableCell>
                            <TableCell>Clip Index</TableCell>
                            <TableCell>Dateiname</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Trainingsrechner-Pfad</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {clips.map((clip) => (
                            <TableRow key={clip._id}>
                              <TableCell>{clip.camera_name}</TableCell>
                              <TableCell>{clip.clip_index}</TableCell>
                              <TableCell>{clip.filename}</TableCell>
                              <TableCell>
                                <Chip
                                  icon={getStatusIcon(clip.status)}
                                  label={clip.status}
                                  color={getStatusColor(clip.status) as any}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" color="text.secondary">
                                  {clip.file_path}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        )}

        {/* Table View */}
        {activeTab === 2 && (
          <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Kamera</TableCell>
                  <TableCell>Clip Index</TableCell>
                  <TableCell>Dateiname</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Datum</TableCell>
                  <TableCell>Erstellt</TableCell>
                  <TableCell>Trainingsrechner-Pfad</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredClips.map((clip) => (
                  <TableRow key={clip._id}>
                    <TableCell>{clip.camera_name}</TableCell>
                    <TableCell>{clip.clip_index}</TableCell>
                    <TableCell>{clip.filename}</TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(clip.status)}
                        label={clip.status}
                        color={getStatusColor(clip.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDate(clip.scheduled_date)}</TableCell>
                    <TableCell>{formatDateTime(clip.created_at)}</TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {clip.file_path}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default ComprehensiveClipView;